import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase } from '../lib/supabase'
import type { User, Session, SupabaseClient } from '@supabase/supabase-js'
import toast from 'react-hot-toast'

// Inline UserProfile type and getUserProfile function to avoid workspace dependency
export interface UserProfile {
  id: string
  email: string
  full_name: string | null
  phone_number: string | null
  user_type: 'customer' | 'restaurant_owner' | 'admin' | null
  status: 'active' | 'inactive' | 'suspended' | null
  created_at: string
  updated_at: string
  preferences?: Record<string, unknown>
}

/**
 * Get current user profile from database
 */
const getUserProfile = async (
  client: SupabaseClient,
  userId: string
): Promise<UserProfile | null> => {
  const { data, error } = await client
    .from('users')
    .select('*')
    .eq('id', userId)
    .single()

  if (error || !data) {
    return null
  }

  return data as UserProfile
}

/**
 * Admin Authentication Store (Zustand)
 *
 * Replaces the Context API-based auth provider with Zustand store pattern.
 * Manages admin authentication state with Supabase Auth.
 *
 * Key Features:
 * - JWT-based authentication via Supabase Auth
 * - Admin-only access (user_type === 'admin')
 * - Persistent session storage via Zustand persist middleware
 * - Automatic session restoration on app reload
 * - Real-time auth state synchronization
 */

interface AuthState {
  // State
  user: User | null
  session: Session | null
  profile: UserProfile | null
  isLoading: boolean
  isAuthenticated: boolean
  isAdmin: boolean
  error: Error | null
  _hasHydrated: boolean  // CRITICAL: Tracks Zustand persist hydration completion

  // Actions
  login: (email: string, password: string) => Promise<boolean>
  logout: () => Promise<void>
  initializeAuth: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      session: null,
      profile: null,
      isLoading: true,
      isAuthenticated: false,
      isAdmin: false,
      error: null,
      _hasHydrated: false,  // Will be set to true by onRehydrateStorage

      /**
       * Login with email and password
       * Verifies admin status after successful authentication
       */
      login: async (email: string, password: string): Promise<boolean> => {
        set({ isLoading: true, error: null })

        try {
          // Authenticate with Supabase
          const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password
          })

          if (error) {
            set({ error: new Error(error.message), isLoading: false })
            toast.error(error.message)
            return false
          }

          if (!data.user || !data.session) {
            set({ error: new Error('Authentication failed'), isLoading: false })
            toast.error('Authentication failed')
            return false
          }

          // Fetch user profile and verify admin status
          const profile = await getUserProfile(supabase, data.user.id)

          if (!profile || profile.user_type !== 'admin') {
            // User is not an admin - sign them out
            await supabase.auth.signOut()
            set({ error: new Error('Admin access required'), isLoading: false })
            toast.error('You do not have admin access to this platform')
            return false
          }

          // Success - update state
          set({
            user: data.user,
            session: data.session,
            profile,
            isAuthenticated: true,
            isAdmin: true,
            isLoading: false,
            error: null
          })

          toast.success(`Welcome back, ${profile.full_name || profile.email}!`)
          return true
        } catch (err) {
          const error = err instanceof Error ? err : new Error('Login failed')
          set({ error, isLoading: false })
          toast.error(error.message)
          return false
        }
      },

      /**
       * Logout current admin user
       */
      logout: async (): Promise<void> => {
        try {
          await supabase.auth.signOut()
          set({
            user: null,
            session: null,
            profile: null,
            isAuthenticated: false,
            isAdmin: false,
            error: null
          })
          toast.success('Logged out successfully')
        } catch (err) {
          const error = err instanceof Error ? err : new Error('Logout failed')
          console.error('[AuthStore] Logout error:', error)
          toast.error('Logout failed')
        }
      },

      /**
       * Initialize auth state on app load
       * Restores session from localStorage if valid
       */
      initializeAuth: async (): Promise<void> => {
        try {
          // Get current session from Supabase
          const { data: { session } } = await supabase.auth.getSession()

          if (session?.user) {
            // Fetch user profile
            const profile = await getUserProfile(supabase, session.user.id)

            // Verify admin status
            if (profile?.user_type === 'admin') {
              set({
                user: session.user,
                session,
                profile,
                isAuthenticated: true,
                isAdmin: true,
                isLoading: false,
                error: null
              })
              return
            } else {
              // User is not admin - sign them out
              await supabase.auth.signOut()
            }
          }

          // No valid session or not admin
          set({
            user: null,
            session: null,
            profile: null,
            isAuthenticated: false,
            isAdmin: false,
            isLoading: false,
            error: null
          })
        } catch (err) {
          const error = err instanceof Error ? err : new Error('Auth initialization failed')
          console.error('[AuthStore] Initialize error:', error)
          set({
            user: null,
            session: null,
            profile: null,
            isAuthenticated: false,
            isAdmin: false,
            isLoading: false,
            error
          })
        }
      }
    }),
    {
      name: 'rsvd-admin-auth',
      // Don't persist sensitive data - Supabase handles session persistence
      partialize: () => ({}),
      onRehydrateStorage: () => {
        console.log('[AuthStore] Starting hydration from localStorage...');
        return (state, error) => {
          // CRITICAL: Always mark hydration as complete, even on error
          // Otherwise AuthGuard waits forever on _hasHydrated
          if (error) {
            console.error('[AuthStore] Hydration error:', error);
            // Still mark as hydrated so app doesn't hang
            // Use queueMicrotask to avoid TDZ errors during store initialization
            queueMicrotask(() => {
              useAuthStore.setState({ _hasHydrated: true });
            });
            return;
          }

          if (!state) {
            // State is null - still mark as hydrated
            // Use queueMicrotask to avoid TDZ errors during store initialization
            queueMicrotask(() => {
              useAuthStore.setState({ _hasHydrated: true });
            });
            return;
          }

          // Mark hydration as complete - MUST use setState
          // Direct mutation doesn't propagate to the store
          // Use queueMicrotask to avoid TDZ errors during store initialization
          queueMicrotask(() => {
            useAuthStore.setState({ _hasHydrated: true });
          });
          console.log('[AuthStore] Hydration complete');
        };
      }
    }
  )
)

/**
 * Listen for auth state changes
 * Automatically updates store when user logs in/out
 *
 * DEBOUNCE: Prevents infinite SIGNED_OUT loop when no session exists.
 * Supabase may fire multiple SIGNED_OUT events rapidly on page load.
 *
 * MEMORY LEAK FIX: Store subscription for cleanup on app unmount.
 * Without cleanup, listeners accumulate during HMR and navigation.
 */
let lastSignedOutTime = 0;
const SIGNED_OUT_DEBOUNCE_MS = 1000; // 1 second debounce

// Store subscription for cleanup
const authStateSubscription = supabase.auth.onAuthStateChange(async (event, session) => {
  console.log('[AuthStore] Auth state changed:', event)

  if (event === 'SIGNED_OUT') {
    // Debounce: Skip if already signed out recently
    const now = Date.now();
    if (now - lastSignedOutTime < SIGNED_OUT_DEBOUNCE_MS) {
      console.log('[AuthStore] Debounced duplicate SIGNED_OUT event');
      return;
    }
    lastSignedOutTime = now;

    // Skip if already signed out (prevent redundant state updates)
    const { isAuthenticated } = useAuthStore.getState();
    if (!isAuthenticated) {
      console.log('[AuthStore] Already signed out, skipping state update');
      return;
    }

    useAuthStore.setState({
      user: null,
      session: null,
      profile: null,
      isAuthenticated: false,
      isAdmin: false,
      error: null
    })
  } else if (event === 'TOKEN_REFRESHED' && session) {
    // Update session on token refresh
    useAuthStore.setState({ session })
  }
})

/**
 * Cleanup auth store subscriptions
 *
 * Call this on app unmount to prevent memory leaks.
 * Usage: useEffect(() => () => cleanupAuthStore(), [])
 */
export const cleanupAuthStore = (): void => {
  if (authStateSubscription?.data?.subscription) {
    authStateSubscription.data.subscription.unsubscribe()
    console.log('[AuthStore] Auth state subscription cleaned up')
  }
}

/**
 * Helper functions for permission and role checking
 * (Compatible with old AuthService pattern)
 */
export const hasPermission = (permission: string): boolean => {
  const { profile, isAdmin } = useAuthStore.getState()
  if (!profile) return false
  if (isAdmin && profile.user_type === 'admin') return true

  // Check user permissions (stored in JSONB preferences field)
  const permissions = (profile.preferences as any)?.permissions || []
  return permissions.includes(permission) || permissions.includes('all')
}

export const hasRole = (role: string): boolean => {
  const { profile } = useAuthStore.getState()
  if (!profile) return false

  // Check user role (stored in JSONB preferences field)
  const userRole = (profile.preferences as any)?.role || 'admin'
  return userRole === role
}
